import { useState } from 'react';
import { motion } from 'framer-motion';
import { FiLock, FiMail, FiUser, FiX } from 'react-icons/fi';
import { useAuth } from '@hooks/useAuth';
import Button from '@components/common/Button';
import Modal from '@components/common/Modal';
import toast from 'react-hot-toast';

const LoginModal = ({ isOpen, onClose }) => {
  const { login } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login({ email: formData.email, password: formData.password });
        toast.success('Successfully logged in!');
        onClose();
        // Reset form
        setFormData({ email: '', password: '', name: '' });
      } else {
        // Register logic would go here
        toast.success('Account created successfully!');
        setIsLogin(true);
      }
    } catch (error) {
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleClose = () => {
    setFormData({ email: '', password: '', name: '' });
    setIsLogin(true);
    onClose();
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      title={isLogin ? 'Sign In to Your Account' : 'Create New Account'}
      size="md"
    >
      <div className="space-y-6">
        {/* Toggle Login/Register */}
        <div className="text-center text-sm text-gray-600">
          {isLogin ? "Don't have an account? " : 'Already have an account? '}
          <button
            onClick={() => setIsLogin(!isLogin)}
            className="font-medium text-accent-600 hover:text-accent-500 transition-colors"
          >
            {isLogin ? 'Register here' : 'Sign in here'}
          </button>
        </div>

        {/* Form */}
        <form className="space-y-5" onSubmit={handleSubmit}>
          {/* Name field (only for registration) */}
          {!isLogin && (
            <div>
              <label htmlFor="name" className="label flex items-center space-x-2">
                <FiUser className="text-gray-500" />
                <span>Full Name</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required={!isLogin}
                value={formData.name}
                onChange={handleChange}
                className="input"
                placeholder="John Doe"
              />
            </div>
          )}

          {/* Email field */}
          <div>
            <label htmlFor="email" className="label flex items-center space-x-2">
              <FiMail className="text-gray-500" />
              <span>Email Address</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="input"
              placeholder="you@example.com"
            />
          </div>

          {/* Password field */}
          <div>
            <label htmlFor="password" className="label flex items-center space-x-2">
              <FiLock className="text-gray-500" />
              <span>Password</span>
            </label>
            <input
              id="password"
              name="password"
              type="password"
              required
              value={formData.password}
              onChange={handleChange}
              className="input"
              placeholder="••••••••"
            />
          </div>

          {/* Remember me & Forgot password (only for login) */}
          {isLogin && (
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <button
                  type="button"
                  onClick={() => {
                    handleClose();
                    window.location.href = '/forgot-password';
                  }}
                  className="font-medium text-accent-600 hover:text-accent-500"
                >
                  Forgot password?
                </button>
              </div>
            </div>
          )}

          {/* Submit button */}
          <Button
            text={loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            type="primary"
            onClick={handleSubmit}
            disabled={loading}
            className="w-full"
          />
        </form>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or continue with</span>
          </div>
        </div>

        {/* Social login buttons */}
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            className="w-full inline-flex justify-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <span className="sr-only">Sign in with Google</span>
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
            </svg>
          </button>

          <button
            type="button"
            className="w-full inline-flex justify-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <span className="sr-only">Sign in with Microsoft</span>
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zm12.6 0H12.6V0H24v11.4z" />
            </svg>
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LoginModal;

